import {
    Box,
    Button,
    Flex,
    FormControl,
    Heading,
    HStack,
    Text,
    useToast,
} from "@chakra-ui/react";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageTaxTypePage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch } = useContext(Store);
  const { getTaxType, addTaxType, updateTaxType, getTaxFrequencies } = useApi();
  const {
    data: taxType,
    isLoading,
    mutate,
  } = useCache("taxType", () => getTaxType(+id || 0));
  const {
    data: taxFrequencies,
    isLoading: isTaxFrequencyLoading,
    mutate: mutateTaxFrequency,
  } = useCache("taxFrequencies", () => getTaxFrequencies(1, ""));

  const initialValues = useMemo(() => {
    return {
      name: taxType?.name || "",
      taxFrequency: taxType?.tax_frequency_id || "",
      submit: null,
    };
  }, [taxType]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Nom requis"),
    taxFrequency: Yup.string().trim().required("fréquence des créances requis"),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let formData: any = new FormData();
        formData.append("name", values.name);
        formData.append("tax_frequency_id", values.taxFrequency);
        formData.append("is_active", true);
        if (id) {
          formData.append("id", id);
        }
        let response: any = null;
        if (id) {
          response = await updateTaxType(formData);
        } else {
          response = await addTaxType(formData);
        }
        if (!response.error_code && !response.errors) {
          if (response.id) {
            dispatch(
              showToast({
                toast: toast,
                message: id
                  ? "Fréquence mise à jour avec succès"
                  : "Fréquence ajoutée avec succès",
                status: toastTypes.success,
              })
            );
            if (id) {
              mutate(getTaxType(+id));
              navigate("/tax-types");
            } else {
              navigate("/tax-types");
            }
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        console.log(ex);
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          height={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier une fréquence" : "Créer une fréquence"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="16px" flexDirection="column">
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Nom</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.name && (touched.name as boolean)}
                      >
                        <AppInput
                          required
                          placeholder={`Nom`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="name"
                          name="name"
                          autoComplete="name"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.name}
                          showErrorMessage={Boolean(
                            touched.name && errors.name
                          )}
                          errorMessage={errors.name}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Fréquence des créances
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.taxFrequency &&
                          (touched.taxFrequency as boolean)
                        }
                      ></FormControl>
                      {!isTaxFrequencyLoading && (
                        <AppSelect
                          id="taxFrequency"
                          name="taxFrequency"
                          onChange={(field: string, value: any) => {
                            setFieldValue(field, value);
                            mutateTaxFrequency(getTaxFrequencies(1, ""));
                          }}
                          value={
                            values.taxFrequency || initialValues.taxFrequency
                          }
                          placeholder="Veuillez sélectionner une période de règlement"
                          options={taxFrequencies}
                          isMulti={false}
                          isLoading={isTaxFrequencyLoading}
                          showErrorMessage={Boolean(touched.taxFrequency && errors.taxFrequency)}
                          errorMessage={errors.taxFrequency}
                          onBlur={handleBlur}
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
